import { TeamsTable } from "@components/table/teams/teams-table";
import { TeamsHeaders } from "@components/table/teams/teams-table-types";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { TeamsDetails } from "@pages/members/teams/teams-details";
import { TeamsEmptyPage } from "@pages/members/teams/teams-empty-page";
import { TeamsPageBulkActions } from "@pages/members/teams/teams-page-bulk-actions";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  fetchingTeamsFlagsSelector,
  teamsSelector,
} from "@store/teams/teams-selector";
import { fetchTeams } from "@store/teams/teams-slice-thunk";
import { searchSelector } from "@store/ui/ui-selector";
import { useEffect, useMemo } from "react";

/**
 * The component that will render the kpi details and the table for the teams
 */
export function Teams({ companyId }: BaseCompanyIdProps): JSX.Element {
  const teams = useAppSelector(teamsSelector);
  const { status } = useAppSelector(fetchingTeamsFlagsSelector);
  const dispatch = useAppDispatch();
  const { debouncedSearchText, isDebouncingSearch } =
    useAppSelector(searchSelector);

  // Constructing the empty page content if should show
  const shouldShowEmptyPage = useMemo(() => {
    return (
      (teams.length === 0 && status === "succeeded") || status === "rejected"
    );
  }, [status, teams.length]);

  // Fetches searched teams
  useEffect(() => {
    async function searchTeams(): Promise<void> {
      await dispatch(
        fetchTeams({
          searchText: debouncedSearchText,
        })
      );
    }

    if (debouncedSearchText.length) {
      void searchTeams();
    }
  }, [debouncedSearchText, dispatch]);

  const buttonComponents = TeamsPageBulkActions({
    companyId,
    teams,
  });

  // If fetching is done and teams are empty, then either no team is present or the search text is not matching any team
  if (shouldShowEmptyPage) {
    return <TeamsEmptyPage status={status} />;
  }

  return (
    <>
      <TeamsDetails />

      <TeamsTable
        companyId={companyId}
        teams={teams}
        isLoading={status === "pending" || isDebouncingSearch}
        requiredColumns={[
          TeamsHeaders.name,
          TeamsHeaders.users,
          TeamsHeaders.creationDate,
          TeamsHeaders.workspaceRole,
          TeamsHeaders.options,
        ]}
        bulkActionButtons={buttonComponents}
      />
    </>
  );
}
